// Export pages as images. The engine already rasterises every page for
// the canvas, so this is mostly plumbing — but two details decide whether
// the result is what the user expected:
//
//  1. It renders a FLATTENED copy of the document, not the live baseline.
//     Annotations live in an overlay, so rasterising the baseline would
//     silently drop every highlight and note. The caller saves with
//     flatten:"all" first and hands those bytes to exportOpen.
//  2. One page downloads as a bare image; several download as a zip.
//     Twelve separate save prompts is not an export.
import type { PdfEngine } from "./engine.ts";

export type ImageFormat = "png" | "jpeg" | "webp";

export const IMAGE_FORMATS: readonly {
  id: ImageFormat;
  label: string;
  ext: string;
  mime: string;
  /** Lossy formats take a quality; PNG ignores it. */
  lossy: boolean;
}[] = [
  { id: "png", label: "PNG", ext: "png", mime: "image/png", lossy: false },
  { id: "jpeg", label: "JPEG", ext: "jpg", mime: "image/jpeg", lossy: true },
  { id: "webp", label: "WebP", ext: "webp", mime: "image/webp", lossy: true },
];

/** Offered resolutions. PDF user space is 72 dpi, so the render scale is
 *  simply dpi / 72 — no calibration, no guessing. */
export const EXPORT_DPI: readonly { dpi: number; label: string; hint: string }[] = [
  { dpi: 72, label: "72 dpi", hint: "screen" },
  { dpi: 150, label: "150 dpi", hint: "good print" },
  { dpi: 300, label: "300 dpi", hint: "high quality" },
  { dpi: 600, label: "600 dpi", hint: "archival — large files" },
];

export function formatOf(id: ImageFormat) {
  return IMAGE_FORMATS.find((f) => f.id === id) ?? IMAGE_FORMATS[0]!;
}

/** Parse a page range the way print dialogs do: "1-3, 5, 8-" over a
 *  1-based document, returning 0-based indices in ascending order with
 *  duplicates removed. An empty or unparseable spec means "everything",
 *  which is the safe reading of an ambiguous range. */
export function parsePageRange(spec: string, pageCount: number): number[] {
  const trimmed = spec.trim();
  if (!trimmed) return Array.from({ length: pageCount }, (_, i) => i);
  const out = new Set<number>();
  for (const part of trimmed.split(",")) {
    const piece = part.trim();
    if (!piece) continue;
    const m = /^(\d*)\s*-\s*(\d*)$/.exec(piece);
    if (m) {
      const from = m[1] ? Number(m[1]) : 1;
      const to = m[2] ? Number(m[2]) : pageCount;
      for (let n = Math.min(from, to); n <= Math.max(from, to); n += 1) {
        if (n >= 1 && n <= pageCount) out.add(n - 1);
      }
      continue;
    }
    const n = Number(piece);
    if (Number.isFinite(n) && n >= 1 && n <= pageCount) out.add(n - 1);
  }
  return out.size === 0
    ? Array.from({ length: pageCount }, (_, i) => i)
    : [...out].sort((a, b) => a - b);
}

/** Zero-padded to the document's width, so "page-09" sorts before
 *  "page-10" in every file manager. */
export function imageFileName(
  base: string,
  pageIndex: number,
  pageCount: number,
  ext: string
): string {
  const width = String(pageCount).length;
  return `${base}-${String(pageIndex + 1).padStart(width, "0")}.${ext}`;
}

/** Raw RGBA from the engine → an encoded image blob. */
async function encode(
  raster: { width: number; height: number; pixels: ArrayBuffer },
  format: ImageFormat,
  quality: number
): Promise<Blob> {
  const { width, height, pixels } = raster;
  const data = new ImageData(new Uint8ClampedArray(pixels), width, height);
  // OffscreenCanvas keeps this off the layout path; the fallback covers
  // browsers without it (Safari < 16.4).
  if (typeof OffscreenCanvas !== "undefined") {
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("could not get a 2D context for the export");
    ctx.putImageData(data, 0, 0);
    return canvas.convertToBlob({ type: formatOf(format).mime, quality });
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("could not get a 2D context for the export");
  ctx.putImageData(data, 0, 0);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("the browser could not encode that image"))),
      formatOf(format).mime,
      quality
    );
  });
}

export interface ImageExportOptions {
  /** Flattened PDF bytes — what the images should actually show. */
  bytes: ArrayBuffer;
  pageIndices: number[];
  format: ImageFormat;
  dpi: number;
  /** 0–1, ignored by PNG. */
  quality: number;
  /** File name stem, without extension. */
  baseName: string;
  onProgress?: (done: number, total: number) => void;
}

export interface ExportedImage {
  name: string;
  blob: Blob;
}

/** Rasterise the requested pages, one at a time. Sequential on purpose:
 *  a 600-dpi A4 page is ~35 MB of RGBA before encoding, and rendering a
 *  whole document in parallel is how you run a tab out of memory. */
export async function exportPageImages(
  engine: PdfEngine,
  opts: ImageExportOptions
): Promise<ExportedImage[]> {
  const { bytes, pageIndices, format, dpi, quality, baseName, onProgress } = opts;
  const { pageCount } = await engine.exportOpen(bytes);
  try {
    const scale = dpi / 72;
    const out: ExportedImage[] = [];
    const wanted = pageIndices.filter((i) => i >= 0 && i < pageCount);
    for (const [n, pageIndex] of wanted.entries()) {
      const raster = await engine.exportRender(pageIndex, scale);
      out.push({
        name: imageFileName(baseName, pageIndex, pageCount, formatOf(format).ext),
        blob: await encode(raster, format, quality),
      });
      onProgress?.(n + 1, wanted.length);
    }
    return out;
  } finally {
    // Release the copy even if a page failed — it's a whole second
    // document's worth of wasm heap.
    await engine.exportClose().catch(() => {});
  }
}

/** One image goes out as itself; several go out as a zip. */
export async function packImages(
  images: ExportedImage[],
  zipName: string
): Promise<{ blob: Blob; name: string }> {
  if (images.length === 1) return { blob: images[0]!.blob, name: images[0]!.name };
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  for (const img of images) zip.file(img.name, img.blob);
  const blob = await zip.generateAsync({ type: "blob" });
  return { blob, name: zipName };
}
